/*
 * <steals-wishlist-page> — Renders the /favoritos page grid.
 * Uses cached handles (from StealsWishlistHandles) for instant loading.
 * Falls back to /products.json only for uncached IDs.
 */

class StealsWishlistPage extends HTMLElement {
  constructor() {
    super();
    this._onChanged = this._onChanged.bind(this);
  }

  connectedCallback() {
    this.contentEl = this.querySelector('[data-wishlist-page-content]');
    this.countEl = this.querySelector('[data-wishlist-page-count]');
    this.collectionUrl = this.dataset.collectionUrl || '/collections/all';

    this._unsubscribe = subscribe('wishlist:changed', this._onChanged);
    this._loadAndRender();
  }

  disconnectedCallback() {
    if (this._unsubscribe) this._unsubscribe();
  }

  async _loadAndRender() {
    const ids = await StealsWishlist.getAll();
    this._updateCount(ids.length);

    if (ids.length === 0) {
      this._renderEmpty();
      return;
    }

    // Try cached handles first (instant)
    const handleCache = window.StealsWishlistHandles ? window.StealsWishlistHandles.getAll() : {};
    let handles = [];
    const uncachedIds = [];

    for (const id of ids) {
      const handle = handleCache[String(id)];
      if (handle) {
        handles.push(handle);
      } else {
        uncachedIds.push(id);
      }
    }

    // Fetch uncached handles via /products.json (only if needed)
    if (uncachedIds.length > 0) {
      const fetched = await this._fetchHandles(uncachedIds);
      handles = handles.concat(fetched);
    }

    if (handles.length === 0) {
      this._renderEmpty();
      return;
    }

    await this._renderCards(handles);
  }

  _updateCount(count) {
    if (!this.countEl) return;
    this.countEl.textContent = count > 0 ? `(${count} ${count === 1 ? 'produto' : 'produtos'})` : '';
  }

  _renderEmpty() {
    this.contentEl.innerHTML = `
      <div class="steals-wishlist-page__empty">
        <p>Voce ainda nao tem favoritos</p>
        <a href="${this.collectionUrl}">Explorar produtos</a>
      </div>
    `;
  }

  async _fetchHandles(ids) {
    const idsSet = new Set(ids.map(String));
    const handles = [];

    try {
      let page = 1;
      let hasMore = true;

      while (hasMore && page <= 10) {
        const res = await fetch(`/products.json?limit=250&page=${page}`);
        if (!res.ok) break;

        const data = await res.json();
        const products = data.products || [];

        for (const p of products) {
          if (idsSet.has(String(p.id))) {
            handles.push(p.handle);
            // Cache for next time
            if (window.StealsWishlistHandles) {
              window.StealsWishlistHandles.set(String(p.id), p.handle);
            }
            idsSet.delete(String(p.id));
          }
        }

        if (idsSet.size === 0 || products.length < 250) {
          hasMore = false;
        }
        page++;
      }
    } catch {
      // Silent fail
    }

    return handles;
  }

  async _renderCards(handles) {
    // Fetch all cards in parallel
    const results = await Promise.allSettled(
      handles.map(handle =>
        fetch(`/products/${handle}?view=wishlist-card`)
          .then(r => r.ok ? r.text().then(html => ({ handle, html })) : null)
      )
    );

    let html = '<div class="steals-wishlist-page__grid">';

    for (const result of results) {
      if (result.status === 'fulfilled' && result.value) {
        const { handle, html: cardHtml } = result.value;
        html += `<div class="steals-wishlist-page__card" data-product-handle="${handle}">${cardHtml}</div>`;
      }
    }

    html += '</div>';
    this.contentEl.innerHTML = html;
  }

  _onChanged(data) {
    if (data.action === 'removed' && data.productId) {
      const btns = this.querySelectorAll('steals-wishlist-button');
      btns.forEach(btn => {
        if (btn.dataset.productId === String(data.productId)) {
          const card = btn.closest('.steals-wishlist-page__card');
          if (card) {
            card.classList.add('steals-wishlist-page__card--removing');
            setTimeout(() => {
              card.remove();
              this._updateCount(StealsWishlist.count());
              if (StealsWishlist.count() === 0) this._renderEmpty();
            }, 300);
          }
        }
      });
    }
  }
}

if (!customElements.get('steals-wishlist-page')) {
  customElements.define('steals-wishlist-page', StealsWishlistPage);
}
